/**
 * Contractor Insurance Quote Wizard — Version C1
 * ======================================================================
* CHAT: https://chatgpt.com/c/689e56c0-648c-8324-8123-3bd174191739
* DATE: 8/19/2025 5AM

* Sections:
 *  1) Contact & Business Identity
 *  2) Business Details (trade, years, license, states)
 *  3) Operations & Exposure (project types, heights, work %)
 *  4) Employees & Subcontractors (counts, payroll, sub %)
 *  5) Coverage Needs (GL limits, tools/equipment, inland marine)
 *  6) Loss History (claims, brief narrative)
 *  7) Remarks (Optional)
 *
 * Visual/UX:
 *  - Dual layouts (Wizard, Vertical); explanations aligned per section.
 *  - Active section = primary header/border.
 *  - Section complete = success tint (bg-success-subtle + border-success).
 *  - Optional sections turn green if any optional field is filled.
 *  - Progress uses REQUIRED & VISIBLE fields only; capped at 99% pre-submit.
 *  - Submit button shows "Processing..." immediately; string-only payload.
 *  - Error UX: "Error: network error, try again in a minute."
 */

import React, { useMemo, useState } from "react";
import { Alert, Badge, Button, Card, Col, Form, ProgressBar, Row } from "react-bootstrap";

/* ============================================================
   DECLARATIONS (config, copy, toggles)
============================================================ */
const VERSION = "vC1";
const SHOW_VERSION = true;
const TEST_MODE = true; // true => logs payload, skips network
const API_GATEWAY_URL =
  "https://your-api-id.execute-api.us-east-1.amazonaws.com/prod/contractor-quote";

// Vertical layout: false => show all explanations; true => only active
const EXPLAIN_HIDE_INACTIVE = false;

// Clamp percentage (0..100) to 0..99 pre-submit
const clampPct = (p: number) => Math.min(99, Math.max(0, Math.round(p)));

/* ---------- Section Titles ---------- */
const sectionTitleContact = "Contact & Business Identity";
const sectionTitleBusiness = "Business Details";
const sectionTitleOperations = "Operations & Exposure";
const sectionTitleStaffing = "Employees & Subcontractors";
const sectionTitleCoverage = "Coverage Needs";
const sectionTitleLossHistory = "Loss History";
const sectionTitleRemarks = "Remarks (Optional)";

/* ---------- Section Descriptions ---------- */
const descriptionContact =
  "Tell us how to reach you and how your business is registered so we can prepare accurate documents and certificates.";

const descriptionBusiness =
  "Share your primary trade, license details, years in business, and where you operate.";

const descriptionOperations =
  "Describe the types of projects and exposures (heights, roofing, residential vs commercial) to estimate risk.";

const descriptionStaffing =
  "Team size, payroll, and subcontractor usage help determine rating and eligibility.";

const descriptionCoverage =
  "Indicate desired coverages and limits (General Liability, tools/equipment) to tailor your quote.";

const descriptionLossHistory =
  "Let us know about recent claims or incidents. A brief description helps underwriters assess risk.";

const descriptionRemarks =
  "Anything else we should know (special certificate wording, additional insured needs, etc.).";

/* ============================================================
   Types
============================================================ */
type ViewMode = "wizard" | "vertical";
export type FormShape = Record<string, unknown>;
export type SetForm = (patch: FormShape) => void;

export interface WizardSection {
  id: string;
  title: string;
  explanation: string;
  enabled?: boolean;
  render: (form: FormShape, setForm: SetForm) => React.ReactNode;
}

/* ============================================================
   Helpers
============================================================ */
const S = (v: unknown): string | null => {
  if (v == null) return null;
  if (typeof v === "string") {
    const t = v.trim();
    return t === "" ? null : t;
  }
  return String(v);
};
const hasVal = (v: unknown): boolean => {
  if (v == null) return false;
  if (typeof v === "string") return v.trim().length > 0;
  return true;
};

/* ============================================================
   Data
============================================================ */
const US_STATES: { value: string; label: string }[] = [
  { value: "AL", label: "Alabama" }, { value: "AK", label: "Alaska" },
  { value: "AZ", label: "Arizona" }, { value: "AR", label: "Arkansas" },
  { value: "CA", label: "California" }, { value: "CO", label: "Colorado" },
  { value: "CT", label: "Connecticut" }, { value: "DE", label: "Delaware" },
  { value: "DC", label: "District of Columbia" }, { value: "FL", label: "Florida" },
  { value: "GA", label: "Georgia" }, { value: "HI", label: "Hawaii" },
  { value: "ID", label: "Idaho" }, { value: "IL", label: "Illinois" },
  { value: "IN", label: "Indiana" }, { value: "IA", label: "Iowa" },
  { value: "KS", label: "Kansas" }, { value: "KY", label: "Kentucky" },
  { value: "LA", label: "Louisiana" }, { value: "ME", label: "Maine" },
  { value: "MD", label: "Maryland" }, { value: "MA", label: "Massachusetts" },
  { value: "MI", label: "Michigan" }, { value: "MN", label: "Minnesota" },
  { value: "MS", label: "Mississippi" }, { value: "MO", label: "Missouri" },
  { value: "MT", label: "Montana" }, { value: "NE", label: "Nebraska" },
  { value: "NV", label: "Nevada" }, { value: "NH", label: "New Hampshire" },
  { value: "NJ", label: "New Jersey" }, { value: "NM", label: "New Mexico" },
  { value: "NY", label: "New York" }, { value: "NC", label: "North Carolina" },
  { value: "ND", label: "North Dakota" }, { value: "OH", label: "Ohio" },
  { value: "OK", label: "Oklahoma" }, { value: "OR", label: "Oregon" },
  { value: "PA", label: "Pennsylvania" }, { value: "RI", label: "Rhode Island" },
  { value: "SC", label: "South Carolina" }, { value: "SD", label: "South Dakota" },
  { value: "TN", label: "Tennessee" }, { value: "TX", label: "Texas" },
  { value: "UT", label: "Utah" }, { value: "VT", label: "Vermont" },
  { value: "VA", label: "Virginia" }, { value: "WA", label: "Washington" },
  { value: "WV", label: "West Virginia" }, { value: "WI", label: "Wisconsin" },
  { value: "WY", label: "Wyoming" },
];

const PROJECT_TYPES = [
  "Residential (new)",
  "Residential (remodel)",
  "Commercial (tenant improvement)",
  "Commercial (ground-up)",
  "Industrial",
  "Service/Handyman",
  "Roofing",
  "Concrete/Asphalt",
  "HVAC/Mechanical",
  "Electrical",
  "Plumbing",
];

/* ============================================================
   Completion Schema
   - REQUIRED & visible keys drive % and completion.
   - OPTIONAL sections (coverage, remarks) go green if any filled.
============================================================ */
type SectionKeys = {
  id: string;
  required: (form: FormShape) => string[];      // visible, required keys
  optional?: (form: FormShape) => string[];     // optional keys -> green if any filled
};

const completionSchema: SectionKeys[] = [
  {
    id: "contact",
    required: () => [
      "contactFirstName", "contactLastName", "contactEmail", "contactPhone",
      "businessName", "entityType",
      "address1", "city", "state", "zip",
      // address2 optional, FEIN optional
    ],
  },
  {
    id: "business",
    required: (form) => [
      "primaryTrade",
      "yearsInBusiness",
      "workStates", // treat multi-select as a single required key (string or csv)
      // licenseNumber optional
    ],
  },
  {
    id: "operations",
    required: (form) => [
      "annualRevenue",
      "percentResidential",
      "percentCommercial",
      "maxJobHeight", // text
      // projectTypes optional (checkboxes -> any selected okay but not required)
    ],
  },
  {
    id: "staffing",
    required: (form) => [
      "numEmployees",
      "annualPayroll",
      "usesSubcontractors", // yes/no
      // percentSubcontracted required only if usesSubcontractors=yes
      ...(((form.usesSubcontractors as string) ?? "no") === "yes" ? ["percentSubcontracted"] : []),
    ],
  },
  {
    id: "coverage",
    required: () => [], // Optional for % complete
    optional: () => [
      "glLimitEachOcc",
      "glAgg",
      "toolsEquipment",
      "equipmentLimit",
      "additionalInsuredNeeded",
    ],
  },
  {
    id: "loss-history",
    required: () => [
      "anyClaimsPast3Years",
      // claimsDescription required only if anyClaimsPast3Years=yes
    ],
    optional: (form) => [
      ...(((form.anyClaimsPast3Years as string) ?? "no") === "yes" ? ["claimsDescription"] : []),
    ],
  },
  {
    id: "remarks",
    required: () => [],
    optional: () => ["remarks"],
  },
];

const requiredKeysForSection = (sectionId: string, form: FormShape): string[] => {
  const entry = completionSchema.find((s) => s.id === sectionId);
  return entry ? entry.required(form) : [];
};
const optionalKeysForSection = (sectionId: string, form: FormShape): string[] => {
  const entry = completionSchema.find((s) => s.id === sectionId);
  return entry?.optional ? entry.optional(form) : [];
};

const sectionIsComplete = (sectionId: string, form: FormShape): boolean => {
  const req = requiredKeysForSection(sectionId, form);
  if (req.length > 0) return req.every((k) => hasVal(form[k]));
  const opt = optionalKeysForSection(sectionId, form);
  return opt.length > 0 && opt.some((k) => hasVal(form[k]));
};

const overallCompletion = (enabledSectionIds: string[], form: FormShape): number => {
  const allReq: string[] = enabledSectionIds.flatMap((id) =>
    requiredKeysForSection(id, form)
  );
  const uniq = Array.from(new Set(allReq));
  if (uniq.length === 0) return 0;
  const filled = uniq.reduce((acc, k) => (hasVal(form[k]) ? acc + 1 : acc), 0);
  return (filled / uniq.length) * 100;
};

/* ============================================================
   Controls
============================================================ */
const WizardControls: React.FC<{
  onPrev: () => void;
  onNext: () => void;
  onSubmit: () => void;
  isFirst: boolean;
  isLast: boolean;
  submitting?: boolean;
}> = ({ onPrev, onNext, onSubmit, isFirst, isLast, submitting }) => (
  <div className="d-flex justify-content-between">
    <Button variant="secondary" onClick={onPrev} disabled={isFirst || !!submitting}>
      Previous
    </Button>
    {isLast ? (
      <Button variant="success" onClick={onSubmit} disabled={!!submitting}>
        {submitting ? "Processing..." : "Submit"}
      </Button>
    ) : (
      <Button variant="primary" onClick={onNext} disabled={!!submitting}>
        Next
      </Button>
    )}
  </div>
);

/* ============================================================
   Dual View Wizard
============================================================ */
export const ContractorWizard_C1: React.FC<{
  mode?: ViewMode;
  sections: WizardSection[];
  initialForm?: FormShape;
  className?: string;
  onSubmit?: (form: FormShape) => void;
  submitting?: boolean;
  submitted?: boolean;
}> = ({
  mode = "wizard",
  sections,
  initialForm = {},
  className,
  onSubmit,
  submitting = false,
  submitted = false,
}) => {
  const enabledSections = useMemo(
    () => sections.filter((s) => s.enabled !== false),
    [sections]
  );

  const [form, setFormState] = useState<FormShape>(initialForm);
  const setForm: SetForm = (patch) => setFormState((prev) => ({ ...prev, ...patch }));

  const [current, setCurrent] = useState(0);
  const [activeId, setActiveId] = useState<string>(enabledSections[0]?.id ?? "");

  const ids = useMemo(() => enabledSections.map((s) => s.id), [enabledSections]);
  const activeIndex = Math.max(0, ids.indexOf(activeId));
  const total = Math.max(1, enabledSections.length);
  const enabledIds = enabledSections.map((s) => s.id);

  const rawPct = overallCompletion(enabledIds, form);
  const pctLabel = submitted ? 100 : clampPct(rawPct);
  const pct = submitted ? 100 : clampPct(rawPct);

  // -------- Vertical View --------
  if (mode === "vertical") {
    return (
      <div className={className}>
        <Card className="position-sticky mb-3" style={{ top: 16, zIndex: 1 }}>
          <Card.Header>
            <div className="d-flex align-items-center justify-content-between">
              <div>
                <strong>
                  Contractor Quote {SHOW_VERSION ? `— ${VERSION}` : ""} · Section {activeIndex + 1} of {total}
                </strong>{" "}
                — {enabledSections[activeIndex]?.title}
              </div>
              <div style={{ minWidth: 180 }}>
                <ProgressBar now={pct} label={`${pctLabel}%`} />
              </div>
            </div>
          </Card.Header>
        </Card>

        {enabledSections.map((s) => {
          const isActive = activeId === s.id;
          const showExplain = isActive || !EXPLAIN_HIDE_INACTIVE;
          const isComplete = sectionIsComplete(s.id, form);

          const cardClasses = [
            "border",
            isComplete ? "border-success bg-success-subtle" : "",
            isActive && !isComplete ? "border-primary" : "",
          ].join(" ").trim();

          const headerClasses = isActive ? "bg-primary text-white" : "";

          return (
            <Row key={s.id} className="mb-3" style={{ scrollMarginTop: 90 }}>
              <Col
                md={8}
                onClick={() => setActiveId(s.id)}
                onFocusCapture={() => setActiveId(s.id)}
              >
                <Card className={cardClasses}>
                  <Card.Header className={headerClasses}>
                    {s.title}
                    {isComplete && !isActive && <Badge bg="success" className="ms-2">Complete</Badge>}
                    {isComplete && isActive && <Badge bg="light" text="dark" className="ms-2">Complete</Badge>}
                  </Card.Header>
                  <Card.Body>{s.render(form, setForm)}</Card.Body>
                </Card>
              </Col>
              <Col md={4}>
                {showExplain && (
                  <Card className={isActive ? "border border-primary" : "border"}>
                    <Card.Header className={isActive ? "bg-primary text-white" : ""}>
                      Section Help
                    </Card.Header>
                    <Card.Body>
                      <strong>{s.title}</strong>
                      <p className="mb-0">{s.explanation}</p>
                    </Card.Body>
                  </Card>
                )}
              </Col>
            </Row>
          );
        })}

        <div className="d-flex justify-content-end mt-3">
          <Button variant="success" onClick={() => onSubmit?.(form)} disabled={!!submitting}>
            {submitting ? "Processing..." : "Submit"}
          </Button>
        </div>
      </div>
    );
  }

  // -------- Wizard View --------
  const step = enabledSections[current];
  const stepComplete = sectionIsComplete(step.id, form);
  const cardClasses = stepComplete ? "border border-success bg-success-subtle" : "";

  return (
    <Card className={`${className ?? ""} ${cardClasses}`}>
      <Card.Header>
        <div className="d-flex align-items-center justify-content-between">
          <div>
            <strong>
              Contractor Quote {SHOW_VERSION ? `— ${VERSION}` : ""} · Step {current + 1} of {total}
            </strong>{" "}
            — {step.title}
          </div>
          <div style={{ minWidth: 160 }}>
            <ProgressBar now={pct} label={`${pctLabel}%`} />
          </div>
        </div>
      </Card.Header>

      <Card.Body>
        {step.render(form, setForm)}

        <Card className="mt-3">
          <Card.Header>Section Help</Card.Header>
          <Card.Body>
            <strong>{step.title}</strong>
            <p className="mb-0">{step.explanation}</p>
          </Card.Body>
        </Card>

        <hr />
        <WizardControls
          onPrev={() => setCurrent((c) => Math.max(0, c - 1))}
          onNext={() => setCurrent((c) => Math.min(total - 1, c + 1))}
          onSubmit={() => onSubmit?.(form)}
          isFirst={current === 0}
          isLast={current === total - 1}
          submitting={submitting}
        />
      </Card.Body>
    </Card>
  );
};

/* ============================================================
   Example Usage — Version C1
============================================================ */
export const ExampleContractorWizard_C1: React.FC = () => {
  const [mode, setMode] = useState<ViewMode>("vertical");
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const sections: WizardSection[] = [
    // 1) Contact & Business Identity
    {
      id: "contact",
      title: sectionTitleContact,
      explanation: descriptionContact,
      enabled: true,
      render: (form, setForm) => (
        <Form>
          <Row className="g-3">
            <Col md={6}>
              <Form.Group controlId="contactFirstName">
                <Form.Label>First Name</Form.Label>
                <Form.Control
                  type="text"
                  value={(form.contactFirstName as string) ?? ""}
                  onChange={(e) => setForm({ contactFirstName: e.target.value })}
                  placeholder="Jane"
                  isValid={hasVal(form.contactFirstName)}
                />
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group controlId="contactLastName">
                <Form.Label>Last Name</Form.Label>
                <Form.Control
                  type="text"
                  value={(form.contactLastName as string) ?? ""}
                  onChange={(e) => setForm({ contactLastName: e.target.value })}
                  placeholder="Doe"
                  isValid={hasVal(form.contactLastName)}
                />
              </Form.Group>
            </Col>

            <Col md={6}>
              <Form.Group controlId="contactEmail">
                <Form.Label>Email</Form.Label>
                <Form.Control
                  type="email"
                  value={(form.contactEmail as string) ?? ""}
                  onChange={(e) => setForm({ contactEmail: e.target.value })}
                  placeholder="you@business.com"
                  isValid={hasVal(form.contactEmail)}
                />
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group controlId="contactPhone">
                <Form.Label>Phone</Form.Label>
                <Form.Control
                  type="text"
                  value={(form.contactPhone as string) ?? ""}
                  onChange={(e) => setForm({ contactPhone: e.target.value })}
                  placeholder="(555) 555-1234"
                  inputMode="tel"
                  isValid={hasVal(form.contactPhone)}
                />
              </Form.Group>
            </Col>

            <Col md={6}>
              <Form.Group controlId="businessName">
                <Form.Label>Business Name</Form.Label>
                <Form.Control
                  type="text"
                  value={(form.businessName as string) ?? ""}
                  onChange={(e) => setForm({ businessName: e.target.value })}
                  placeholder="ABC Contracting LLC"
                  isValid={hasVal(form.businessName)}
                />
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group controlId="entityType">
                <Form.Label>Entity Type</Form.Label>
                <Form.Select
                  value={(form.entityType as string) ?? ""}
                  onChange={(e) => setForm({ entityType: e.target.value })}
                  isValid={hasVal(form.entityType)}
                >
                  <option value="">Select...</option>
                  <option value="soleProp">Sole Proprietor</option>
                  <option value="partnership">Partnership</option>
                  <option value="llc">LLC</option>
                  <option value="corp">Corporation</option>
                </Form.Select>
              </Form.Group>
            </Col>

            <Col md={6}>
              <Form.Group controlId="address1Input">
                <Form.Label>Address Line 1</Form.Label>
                <Form.Control
                  id="address1Input"
                  type="text"
                  value={(form.address1 as string) ?? ""}
                  onChange={(e) => setForm({ address1: e.target.value })}
                  placeholder="123 Main St"
                  autoComplete="address-line1"
                  isValid={hasVal(form.address1)}
                />
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group controlId="address2Input">
                <Form.Label>Address Line 2 (optional)</Form.Label>
                <Form.Control
                  id="address2Input"
                  type="text"
                  value={(form.address2 as string) ?? ""}
                  onChange={(e) => setForm({ address2: e.target.value })}
                  placeholder="Suite / Unit"
                  autoComplete="address-line2"
                  isValid={hasVal(form.address2)}
                />
              </Form.Group>
            </Col>

            <Col md={6}>
              <Form.Group controlId="city">
                <Form.Label>City</Form.Label>
                <Form.Control
                  type="text"
                  value={(form.city as string) ?? ""}
                  onChange={(e) => setForm({ city: e.target.value })}
                  isValid={hasVal(form.city)}
                />
              </Form.Group>
            </Col>
            <Col md={3}>
              <Form.Group controlId="state">
                <Form.Label>State</Form.Label>
                <Form.Select
                  value={(form.state as string) ?? ""}
                  onChange={(e) => setForm({ state: e.target.value })}
                  isValid={hasVal(form.state)}
                >
                  <option value="">Select a state…</option>
                  {US_STATES.map((s) => (
                    <option key={s.value} value={s.value}>{s.label}</option>
                  ))}
                </Form.Select>
              </Form.Group>
            </Col>
            <Col md={3}>
              <Form.Group controlId="zip">
                <Form.Label>ZIP</Form.Label>
                <Form.Control
                  type="text"
                  value={(form.zip as string) ?? ""}
                  onChange={(e) => setForm({ zip: e.target.value })}
                  placeholder="80202"
                  inputMode="numeric"
                  isValid={hasVal(form.zip)}
                />
              </Form.Group>
            </Col>

            <Col md={6}>
              <Form.Group controlId="fein">
                <Form.Label>FEIN (optional)</Form.Label>
                <Form.Control
                  type="text"
                  value={(form.fein as string) ?? ""}
                  onChange={(e) => setForm({ fein: e.target.value })}
                  placeholder="12-3456789"
                  isValid={hasVal(form.fein)}
                />
              </Form.Group>
            </Col>
          </Row>
        </Form>
      ),
    },

    // 2) Business Details
    {
      id: "business",
      title: sectionTitleBusiness,
      explanation: descriptionBusiness,
      enabled: true,
      render: (form, setForm) => (
        <Form>
          <Row className="g-3">
            <Col md={6}>
              <Form.Group controlId="primaryTrade">
                <Form.Label>Primary Trade</Form.Label>
                <Form.Control
                  type="text"
                  value={(form.primaryTrade as string) ?? ""}
                  onChange={(e) => setForm({ primaryTrade: e.target.value })}
                  placeholder="e.g. General Contractor, Electrical, Plumbing"
                  isValid={hasVal(form.primaryTrade)}
                />
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group controlId="yearsInBusiness">
                <Form.Label>Years in Business</Form.Label>
                <Form.Control
                  type="text"
                  value={(form.yearsInBusiness as string) ?? ""}
                  onChange={(e) => setForm({ yearsInBusiness: e.target.value })}
                  placeholder="e.g. 5"
                  inputMode="numeric"
                  isValid={hasVal(form.yearsInBusiness)}
                />
              </Form.Group>
            </Col>

            <Col md={6}>
              <Form.Group controlId="licenseNumber">
                <Form.Label>Contractor License # (optional)</Form.Label>
                <Form.Control
                  type="text"
                  value={(form.licenseNumber as string) ?? ""}
                  onChange={(e) => setForm({ licenseNumber: e.target.value })}
                  placeholder="State License #"
                  isValid={hasVal(form.licenseNumber)}
                />
              </Form.Group>
            </Col>

            <Col md={6}>
              <Form.Group controlId="workStates">
                <Form.Label>States of Operation</Form.Label>
                <Form.Select
                  multiple
                  value={((form.workStates as string[]) ?? [])}
                  onChange={(e) => {
                    const opts = Array.from(e.target.selectedOptions).map(o => o.value);
                    setForm({ workStates: opts });
                  }}
                  isValid={Array.isArray(form.workStates) && (form.workStates as string[]).length > 0}
                >
                  {US_STATES.map((s) => (
                    <option key={s.value} value={s.value}>{s.label}</option>
                  ))}
                </Form.Select>
                <div className="form-text">Hold Ctrl/Cmd to select multiple.</div>
              </Form.Group>
            </Col>
          </Row>
        </Form>
      ),
    },

    // 3) Operations & Exposure
    {
      id: "operations",
      title: sectionTitleOperations,
      explanation: descriptionOperations,
      enabled: true,
      render: (form, setForm) => (
        <Form>
          <Row className="g-3">
            <Col md={6}>
              <Form.Group controlId="annualRevenue">
                <Form.Label>Estimated Annual Revenue</Form.Label>
                <Form.Control
                  type="text"
                  value={(form.annualRevenue as string) ?? ""}
                  onChange={(e) => setForm({ annualRevenue: e.target.value })}
                  placeholder="e.g. 750000"
                  inputMode="numeric"
                  isValid={hasVal(form.annualRevenue)}
                />
              </Form.Group>
            </Col>

            <Col md={3}>
              <Form.Group controlId="percentResidential">
                <Form.Label>% Residential</Form.Label>
                <Form.Control
                  type="text"
                  value={(form.percentResidential as string) ?? ""}
                  onChange={(e) => setForm({ percentResidential: e.target.value })}
                  placeholder="e.g. 70"
                  inputMode="numeric"
                  isValid={hasVal(form.percentResidential)}
                />
              </Form.Group>
            </Col>
            <Col md={3}>
              <Form.Group controlId="percentCommercial">
                <Form.Label>% Commercial</Form.Label>
                <Form.Control
                  type="text"
                  value={(form.percentCommercial as string) ?? ""}
                  onChange={(e) => setForm({ percentCommercial: e.target.value })}
                  placeholder="e.g. 30"
                  inputMode="numeric"
                  isValid={hasVal(form.percentCommercial)}
                />
              </Form.Group>
            </Col>

            <Col md={6}>
              <Form.Group controlId="maxJobHeight">
                <Form.Label>Maximum Job Height (ft)</Form.Label>
                <Form.Control
                  type="text"
                  value={(form.maxJobHeight as string) ?? ""}
                  onChange={(e) => setForm({ maxJobHeight: e.target.value })}
                  placeholder="e.g. 35"
                  inputMode="numeric"
                  isValid={hasVal(form.maxJobHeight)}
                />
              </Form.Group>
            </Col>

            <Col md={12}>
              <Form.Group controlId="projectTypes">
                <Form.Label>Project Types (check all that apply)</Form.Label>
                <div className="d-flex flex-wrap gap-3">
                  {PROJECT_TYPES.map((pt) => {
                    const selected = new Set<string>((form.projectTypes as string[]) ?? []);
                    const checked = selected.has(pt);
                    return (
                      <Form.Check
                        key={pt}
                        type="checkbox"
                        id={`pt-${pt}`}
                        label={pt}
                        checked={checked}
                        onChange={(e) => {
                          const set = new Set<string>((form.projectTypes as string[]) ?? []);
                          if (e.target.checked) set.add(pt);
                          else set.delete(pt);
                          setForm({ projectTypes: Array.from(set) });
                        }}
                      />
                    );
                  })}
                </div>
                <div className="form-text">Optional but helpful for underwriting.</div>
              </Form.Group>
            </Col>
          </Row>
        </Form>
      ),
    },

    // 4) Employees & Subcontractors
    {
      id: "staffing",
      title: sectionTitleStaffing,
      explanation: descriptionStaffing,
      enabled: true,
      render: (form, setForm) => {
        const usesSubs = ((form.usesSubcontractors as string) ?? "no") === "yes";
        return (
          <Form>
            <Row className="g-3">
              <Col md={4}>
                <Form.Group controlId="numEmployees">
                  <Form.Label># of W-2 Employees</Form.Label>
                  <Form.Control
                    type="text"
                    value={(form.numEmployees as string) ?? ""}
                    onChange={(e) => setForm({ numEmployees: e.target.value })}
                    placeholder="e.g. 4"
                    inputMode="numeric"
                    isValid={hasVal(form.numEmployees)}
                  />
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group controlId="annualPayroll">
                  <Form.Label>Estimated Annual Payroll ($)</Form.Label>
                  <Form.Control
                    type="text"
                    value={(form.annualPayroll as string) ?? ""}
                    onChange={(e) => setForm({ annualPayroll: e.target.value })}
                    placeholder="e.g. 250000"
                    inputMode="numeric"
                    isValid={hasVal(form.annualPayroll)}
                  />
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group controlId="usesSubcontractors">
                  <Form.Label>Use Subcontractors?</Form.Label>
                  <Form.Select
                    value={(form.usesSubcontractors as string) ?? "no"}
                    onChange={(e) => setForm({ usesSubcontractors: e.target.value })}
                    isValid={hasVal(form.usesSubcontractors)}
                  >
                    <option value="no">No</option>
                    <option value="yes">Yes</option>
                  </Form.Select>
                </Form.Group>
              </Col>

              {usesSubs && (
                <Col md={6}>
                  <Form.Group controlId="percentSubcontracted">
                    <Form.Label>% of Work Subcontracted</Form.Label>
                    <Form.Control
                      type="text"
                      value={(form.percentSubcontracted as string) ?? ""}
                      onChange={(e) => setForm({ percentSubcontracted: e.target.value })}
                      placeholder="e.g. 40"
                      inputMode="numeric"
                      isValid={hasVal(form.percentSubcontracted)}
                    />
                  </Form.Group>
                </Col>
              )}
            </Row>
          </Form>
        );
      },
    },

    // 5) Coverage Needs (Optional turns green if any filled)
    {
      id: "coverage",
      title: sectionTitleCoverage,
      explanation: descriptionCoverage,
      enabled: true,
      render: (form, setForm) => (
        <Form>
          <Row className="g-3">
            <Col md={6}>
              <Form.Group controlId="glLimitEachOcc">
                <Form.Label>GL Each Occurrence Limit</Form.Label>
                <Form.Select
                  value={(form.glLimitEachOcc as string) ?? ""}
                  onChange={(e) => setForm({ glLimitEachOcc: e.target.value })}
                  isValid={hasVal(form.glLimitEachOcc)}
                >
                  <option value="">Select…</option>
                  <option value="1M">$1,000,000</option>
                  <option value="2M">$2,000,000</option>
                </Form.Select>
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group controlId="glAgg">
                <Form.Label>GL General Aggregate</Form.Label>
                <Form.Select
                  value={(form.glAgg as string) ?? ""}
                  onChange={(e) => setForm({ glAgg: e.target.value })}
                  isValid={hasVal(form.glAgg)}
                >
                  <option value="">Select…</option>
                  <option value="2M">$2,000,000</option>
                  <option value="4M">$4,000,000</option>
                </Form.Select>
              </Form.Group>
            </Col>

            <Col md={6}>
              <Form.Group controlId="toolsEquipment">
                <Form.Label>Tools/Equipment Coverage</Form.Label>
                <Form.Select
                  value={(form.toolsEquipment as string) ?? ""}
                  onChange={(e) => setForm({ toolsEquipment: e.target.value })}
                  isValid={hasVal(form.toolsEquipment)}
                >
                  <option value="">Select…</option>
                  <option value="no">No</option>
                  <option value="yes">Yes</option>
                </Form.Select>
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group controlId="equipmentLimit">
                <Form.Label>Equipment Limit (if yes)</Form.Label>
                <Form.Control
                  type="text"
                  value={(form.equipmentLimit as string) ?? ""}
                  onChange={(e) => setForm({ equipmentLimit: e.target.value })}
                  placeholder="e.g. 50000"
                  inputMode="numeric"
                  isValid={hasVal(form.equipmentLimit)}
                />
              </Form.Group>
            </Col>

            <Col md={12}>
              <Form.Group controlId="additionalInsuredNeeded">
                <Form.Label>Additional Insured / Waiver of Subrogation Needed?</Form.Label>
                <Form.Select
                  value={(form.additionalInsuredNeeded as string) ?? ""}
                  onChange={(e) => setForm({ additionalInsuredNeeded: e.target.value })}
                  isValid={hasVal(form.additionalInsuredNeeded)}
                >
                  <option value="">Select…</option>
                  <option value="no">No</option>
                  <option value="yes">Yes</option>
                </Form.Select>
                <div className="form-text">If yes, include wording in Remarks or Loss History.</div>
              </Form.Group>
            </Col>
          </Row>
        </Form>
      ),
    },

    // 6) Loss History
    {
      id: "loss-history",
      title: sectionTitleLossHistory,
      explanation: descriptionLossHistory,
      enabled: true,
      render: (form, setForm) => {
        const anyClaims = ((form.anyClaimsPast3Years as string) ?? "no") === "yes";
        return (
          <Form>
            <Row className="g-3">
              <Col md={6}>
                <Form.Group controlId="anyClaimsPast3Years">
                  <Form.Label>Any Claims in Past 3 Years?</Form.Label>
                  <Form.Select
                    value={(form.anyClaimsPast3Years as string) ?? "no"}
                    onChange={(e) => setForm({ anyClaimsPast3Years: e.target.value, claimsDescription: "" })}
                    isValid={hasVal(form.anyClaimsPast3Years)}
                  >
                    <option value="no">No</option>
                    <option value="yes">Yes</option>
                  </Form.Select>
                </Form.Group>
              </Col>

              {anyClaims && (
                <Col md={12}>
                  <Form.Group controlId="claimsDescription">
                    <Form.Label>Brief Claim Description(s)</Form.Label>
                    <Form.Control
                      as="textarea"
                      rows={4}
                      value={(form.claimsDescription as string) ?? ""}
                      onChange={(e) => setForm({ claimsDescription: e.target.value })}
                      placeholder="Dates, type of claim, amounts paid or reserved, preventive steps taken..."
                      isValid={hasVal(form.claimsDescription)}
                    />
                  </Form.Group>
                </Col>
              )}
            </Row>
          </Form>
        );
      },
    },

    // 7) Remarks (Optional)
    {
      id: "remarks",
      title: sectionTitleRemarks,
      explanation: descriptionRemarks,
      enabled: true,
      render: (form, setForm) => (
        <Form>
          <Row className="g-3">
            <Col md={12}>
              <Form.Group controlId="remarks">
                <Form.Label>Remarks</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={4}
                  value={(form.remarks as string) ?? ""}
                  onChange={(e) => setForm({ remarks: e.target.value })}
                  placeholder="Certificates, additional insured wording, special requirements..."
                  isValid={hasVal(form.remarks)}
                />
              </Form.Group>
            </Col>
          </Row>
        </Form>
      ),
    },
  ];

  const handleSubmit = async (payload: FormShape) => {
    setSubmitError(null);
    setSubmitting(true); // flip button text immediately

    const enabledIds = sections.filter(s => s.enabled !== false).map(s => s.id);
    const raw = overallCompletion(enabledIds, payload); // 0..100
    const pctToSend = clampPct(raw);

    const customer_DATA = {
      // Meta
      submittedAtIso: new Date().toISOString(),
      source: `Contractor Quote Wizard — ${VERSION}`,

      // Contact & Business Identity
      contactFirstName: S(payload.contactFirstName),
      contactLastName: S(payload.contactLastName),
      contactEmail: S(payload.contactEmail),
      contactPhone: S(payload.contactPhone),
      businessName: S(payload.businessName),
      entityType: S(payload.entityType),
      fein: S(payload.fein),

      address1: S(payload.address1),
      address2: S(payload.address2),
      city: S(payload.city),
      state: S(payload.state),
      zip: S(payload.zip),

      // Business Details
      primaryTrade: S(payload.primaryTrade),
      yearsInBusiness: S(payload.yearsInBusiness),
      licenseNumber: S(payload.licenseNumber),
      workStates: Array.isArray(payload.workStates)
        ? (payload.workStates as string[]).join(",")
        : S(payload.workStates),

      // Operations & Exposure
      annualRevenue: S(payload.annualRevenue),
      percentResidential: S(payload.percentResidential),
      percentCommercial: S(payload.percentCommercial),
      maxJobHeight: S(payload.maxJobHeight),
      projectTypes: Array.isArray(payload.projectTypes)
        ? (payload.projectTypes as string[]).join(",")
        : S(payload.projectTypes),

      // Employees & Subcontractors
      numEmployees: S(payload.numEmployees),
      annualPayroll: S(payload.annualPayroll),
      usesSubcontractors: S(payload.usesSubcontractors),
      percentSubcontracted: S(payload.percentSubcontracted),

      // Coverage Needs
      glLimitEachOcc: S(payload.glLimitEachOcc),
      glAgg: S(payload.glAgg),
      toolsEquipment: S(payload.toolsEquipment),
      equipmentLimit: S(payload.equipmentLimit),
      additionalInsuredNeeded: S(payload.additionalInsuredNeeded),

      // Loss History
      anyClaimsPast3Years: S(payload.anyClaimsPast3Years),
      claimsDescription: S(payload.claimsDescription),

      // Remarks
      remarks: S(payload.remarks),

      // Progress
      formCompletionPercent: String(pctToSend),
    };

    if (TEST_MODE) {
      console.log("TEST MODE — customer_DATA:", customer_DATA);
      setSubmitting(false);
      return;
    }

    try {
      const resp = await fetch(API_GATEWAY_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(customer_DATA),
      });

      if (!resp.ok) throw new Error("network");

      setSubmitted(true);
    } catch {
      setSubmitError("Error: network error, try again in a minute.");
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className="container my-5">
        <Row className="justify-content-center">
          <Col md={8}>
            <Card>
              <Card.Header className="fw-bold">Thank you!</Card.Header>
              <Card.Body>
                <p className="mb-0">
                  An insurance agent will reach out to you to review and complete your application.
                </p>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </div>
    );
  }

  return (
    <div className="container my-4">
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h3 className="m-0">
          Contractor Quote {SHOW_VERSION ? `— ${VERSION}` : ""}
        </h3>
        <div className="d-flex gap-2">
          <Button
            variant={mode === "wizard" ? "primary" : "outline-primary"}
            onClick={() => setMode("wizard")}
            disabled={submitting}
          >
            Wizard View
          </Button>
          <Button
            variant={mode === "vertical" ? "primary" : "outline-primary"}
            onClick={() => setMode("vertical")}
            disabled={submitting}
          >
            Vertical View
          </Button>
        </div>
      </div>

      {submitError && <Alert variant="danger" className="mb-3">{submitError}</Alert>}

      <ContractorWizard_C1
        mode={mode}
        sections={sections}
        initialForm={{
          usesSubcontractors: "no",
          anyClaimsPast3Years: "no",
        }}
        onSubmit={handleSubmit}
        submitting={submitting}
        submitted={false /* handled above */}
      />
    </div>
  );
};

export default ExampleContractorWizard_C1;
